import {
  Box,
  Pagination,
  PaginationItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import styles from '../Person.module.scss';
import { memo, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BaseInput } from '~/components/common';
import { ArrowLeft1Icon, ArrowRight1Icon, ArrowRight3Icon, SearchIcon } from '~/assets/icons';
import { useAppSelector } from '~/redux/hooks';
import { PAGE_SIZE } from '~/utils/constants/common';
import useDebounce from '~/hooks/useDebounce';
import { cast, handleApiError } from '~/utils/helper';
import dayjs from 'dayjs';
import alertApi from '~/apis/alert';
import { IAlertList } from '~/utils/interface/alert';

const cx = classNames.bind(styles);

const PersonLocationHistory = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('admin/person');

  const { selectedUser, userProfile } = useAppSelector((state) => state.auth);

  const username = selectedUser?.username ?? userProfile?.username;

  const [alertList, setAlertList] = useState<Array<IAlertList>>([]);

  const [totalItems, setTotalItems] = useState<number>(0);
  const [pageIndex, setPageIndex] = useState<number>(1);
  const [pageSize] = useState<number>(PAGE_SIZE);

  const [searchInput, setSearchInput] = useState<string>('');
  const searchInputDebounce = useDebounce(searchInput);

  useEffect(() => {
    setPageIndex(1);
  }, [searchInputDebounce]);

  useEffect(() => {
    if (!id || !username) return;

    const getAlertList = async () => {
      try {
        const response = await alertApi.getlist({
          username: userProfile.username,
          personId: id,
          search: searchInputDebounce,
          type: 2,
          pageIndex: pageIndex,
          pageSize: PAGE_SIZE,
        });

        const data = response?.data?.result;

        setAlertList(Array.isArray(data?.data) ? data.data : []);
        setTotalItems(data?.total_record ?? 0);
      } catch (error) {
        handleApiError(error);
      }
    };

    getAlertList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, username, pageIndex, pageSize, searchInputDebounce]);

  return (
    <Box className={cx('person-location-history')}>
      <Box className='flex items-center bg-[#123B6D] h-20 px-6'>
        <Typography
          className='text-white font-semibold md:text-lg lg:text-xl hover:cursor-pointer'
          onClick={() => navigate(`/admin/persons/${id}`)}
        >
          {t('detail.title')}
        </Typography>
        <Box className='w-8 h-8 flex items-center justify-center'>
          <ArrowRight3Icon stroke='#fff' />
        </Box>
        <Typography className='text-white font-semibold md:text-lg lg:text-xl'>
          {t('detail.person_location_history')}
        </Typography>
      </Box>
      <Box className='p-6'>
        <BaseInput
          iconStart={<SearchIcon className='w-4' />}
          placeholder={t('search2', { ns: 'admin/common' })}
          className='w-96 bg-white mb-4 rounded-xl'
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <Box className='bg-white rounded-b-xl rounded-t-xl'>
          <TableContainer className='rounded-t-xl border-b-0 bg-white overflow-x-auto'>
            <Table stickyHeader className='min-w-max'>
              <TableHead>
                <TableRow>
                  <TableCell className='bg-[#DCE8F5] text-[#161A1F] text-sm font-semibold w-52' align='center'>
                    {t('day')}
                  </TableCell>
                  <TableCell
                    className='bg-[#DCE8F5] text-[#161A1F] text-sm font-semibold border-x border-[#EBF1F8] border-b-0 w-44'
                    align='center'
                  >
                    {t('hour')}
                  </TableCell>
                  <TableCell className='bg-[#DCE8F5] text-sm font-semibold text-[#161A1F] w-80' align='center'>
                    {t('detail.location.name')}
                  </TableCell>
                  <TableCell
                    className='bg-[#DCE8F5] text-sm font-semibold text-[#161A1F] w-80 border-x border-[#EBF1F8] border-b-0'
                    align='center'
                  >
                    {t('detail.location.type.title')}
                  </TableCell>
                  <TableCell className='bg-[#DCE8F5] text-sm font-semibold text-[#161A1F] w-fit' align='center'>
                    {t('description')}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {alertList.length > 0 ? (
                  alertList.map((row: IAlertList, index: number) => (
                    <TableRow key={`${row.id}-${index}`}>
                      <TableCell
                        className='text-[#404854] text-base font-medium border-x border-[#EBF1F8]'
                        align='center'
                      >
                        <Typography className='text-[#161A1F] text-base'>
                          {row.created_at ? dayjs(row.created_at).format('DD/MM/YYYY') : ''}
                        </Typography>
                      </TableCell>
                      <TableCell
                        className='text-[#404854] text-base font-medium border-r border-[#EBF1F8]'
                        align='center'
                      >
                        <Typography className='text-[#161A1F] text-base'>
                          {row.created_at ? dayjs(row.created_at).format('HH:mm') : ''}
                        </Typography>
                      </TableCell>
                      <TableCell
                        className='text-[#404854] text-base font-medium border-r border-[#EBF1F8]'
                        align='center'
                      >
                        <Typography className='text-[#161A1F] text-base'>{cast(row.location_name)}</Typography>
                      </TableCell>
                      <TableCell
                        className='text-[#404854] text-base font-medium border-r border-[#EBF1F8]'
                        align='center'
                      >
                        <Typography className='text-[#161A1F] text-base'>
                          {t('detail.location.type.' + cast(row.location_type))}
                        </Typography>
                      </TableCell>
                      <TableCell className='text-[#404854] text-base font-medium border-r border-[#EBF1F8]'>
                        <Typography className='text-[#161A1F] text-base'>{cast(row.content)}</Typography>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align='center' className='border-0 text-base text-[#404854]'>
                      {t('noData', { ns: 'admin/common' })}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box className='flex justify-center items-center py-2'>
            <Pagination
              disabled={!totalItems}
              count={Math.ceil((totalItems ?? 0) / pageSize)}
              variant='outlined'
              page={pageIndex ?? 1}
              onChange={(e, value: number) => setPageIndex(value)}
              renderItem={(item) => (
                <PaginationItem
                  className='border-none text-[#7c8796] text-base rounded-xl mx-3'
                  slots={{
                    previous: () => <ArrowLeft1Icon className='w-3 h-3 text-[#7c8796] hover:opacity-70' />,
                    next: () => <ArrowRight1Icon className='w-3 h-3 text-[#7c8796] hover:opacity-70' />,
                  }}
                  {...item}
                />
              )}
              shape='rounded'
              sx={{
                '& .MuiPaginationItem-root.Mui-selected': {
                  backgroundColor: '#e3e7ec',
                  borderRadius: '12px',
                  color: '#161a1f',
                  fontSize: '16px',
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default memo(PersonLocationHistory);
